import { useEffect } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import {
  auditActorIdsNeedingNames,
  ENTITY_AUDIT_STALE_TIME_MS,
  entityAuditQueryKey,
  entityAuditRefetchInterval,
  isEntityAuditRefreshEvent,
  listEntityAudit,
  PlaybookApiError,
  useApiClient,
  withResolvedAuditActorNames,
} from "@playbookai/api-clients";
import { useKpiActorUserId } from "./kpiAuthScope.js";
import { useOwnerNames } from "./useOwnerNames.js";

/** Read-only KPI governance activity over the shared entity audit endpoint. */
export function useKpiAudit(kpiId: string | undefined) {
  const config = useApiClient();

  /* The reader's own action must not wait out the poll here either. The four
     History rails got this and this Card did not — the same omission as the
     polling fix, on the same surface, so it is closed in the same breath
     rather than left as a second inconsistency. KPI governance actions are
     ordinary mutations, so a succeeded one means this feed is already
     stale. */
  const queryClient = useQueryClient();
  const auditKey = entityAuditQueryKey(config, "kpi", kpiId ?? "");
  const keyId = auditKey.join("|");
  useEffect(() => {
    if (!kpiId) return;
    return queryClient.getMutationCache().subscribe((event) => {
      if (!isEntityAuditRefreshEvent(event)) return;
      void queryClient.invalidateQueries({ queryKey: auditKey });
    });
    // `auditKey` is rebuilt each render; `keyId` is its stable identity.
  }, [kpiId, queryClient, keyId]);

  const feed = useInfiniteQuery({
    queryKey: entityAuditQueryKey(config, "kpi", kpiId ?? ""),
    queryFn: ({ pageParam }: { pageParam: string | undefined }) =>
      listEntityAudit(config, {
        entityType: "kpi",
        entityId: kpiId!,
        ...(pageParam ? { cursor: pageParam } : {}),
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) =>
      last.hasMore ? (last.nextCursor ?? undefined) : undefined,
    enabled: Boolean(kpiId),
    /* The shared audit-contract rules, not a local copy. This hook held the
       SECOND byte-identical `30_000` — the KPI activity Card is a fifth
       consumer of the same contract as the four rails, and was missed when
       they were fixed, so it kept shipping the no-refresh bug. Worse here
       than on a rail: the Card is always mounted, so the close-and-reopen
       workaround does not exist and a reader must navigate away and back. */
    staleTime: ENTITY_AUDIT_STALE_TIME_MS,
    refetchInterval: entityAuditRefetchInterval,
    retry: (failureCount, error) =>
      !(
        error instanceof PlaybookApiError &&
        (error.status === 403 || error.status === 404)
      ) && failureCount < 2,
  });

  /* PB-5204 — `/v1/audit` sends `actorName: null` on every HUMAN row BY DESIGN
     (`audit-entry.response.ts`), so without this every author on the Card reads
     the mapper's "A member". The four History rails resolve against a resource
     ROSTER; this Card has none to intersect with — there is no `KpiMembership`,
     and a KPI's audit feed is tenant-member readable — so it resolves through
     the SAME host tenant `PeopleSource` the Owner chips and the value
     history already use (the package is data-blind about identity; the shell
     supplies it). No widening: the feed already handed the caller these ids.

     Resolution lives HERE and not in `KpiActivitySection` because the id set is
     a property of every loaded page, which only this hook holds.

     `useKpiActorUserId()` — not `config.getCurrentUser`, which is the older
     non-reactive seam — keeps the reader's own id OUT of the request: the
     mapper already renders them as "You"/their own name, and resolving it would
     spend a lookup to overwrite that.

     NO memoisation, deliberately: `useOwnerNames` re-dedupes and SORTS, so the
     query key is a pure function of the id SET and TanStack hashes it
     structurally — a 5s poll returning the same actors produces an identical
     hash and no request. A `useMemo` here would change nothing but the reading. */
  const rows = feed.data?.pages.flatMap((p) => p.entries) ?? [];
  const actorUserId = useKpiActorUserId();
  const actorIds = auditActorIdsNeedingNames(rows, actorUserId);
  const actorNames = useOwnerNames(actorIds);

  return {
    ...feed,
    entries: withResolvedAuditActorNames(
      rows,
      new Map(
        Object.entries(actorNames.data ?? {}).map(([id, person]) => [
          id,
          person.name,
        ]),
      ),
      actorUserId,
    ),
    /* Swallowed for the FEED, reported BESIDE it. A directory hiccup must never
       blank — or error — a history that is otherwise perfectly readable, which
       is why this never reaches `feed.isError`; but saying nothing would leave
       "A member" ambiguous between "this person left the tenant" (permanent and
       correct) and "the lookup failed" (transient). `KpiActivitySection` turns
       this into `AuditFeed`'s non-blocking `notice`.

       COVERAGE, not just `isError`. TanStack sets `status: "error"` when a
       BACKGROUND refetch fails even though it retains the last good `data` —
       that is the case `isRefetchError` exists to name — so keying the notice
       on `isError` alone would warn about missing names while every row on
       screen still shows its real one. Warn only when the failure actually
       costs the reader a name. Both halves are load-bearing: without the error
       check a legitimately unnameable id (someone who has left the tenant)
       would raise a permanent false warning, and without the coverage check a
       transient refetch failure would. False while the first lookup is still in
       flight, so a first paint shows no warning. */
    actorNamesPartial:
      actorNames.isError &&
      actorIds.some((id) => actorNames.data?.[id] === undefined),
  };
}
